import { Link } from "react-router-dom";
import { useEffect, useRef, useState, useCallback } from "react";
import Icon from "@/components/feature/Icon";
import SmartImage from "@/components/feature/SmartImage";
import { useSiteSettings } from "@/lib/siteSettings";

/* ─── Canvas Particle Network ─── */
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  opacity: number;
}

interface LightStream {
  x: number;
  y: number;
  speed: number;
  length: number;
  width: number;
  opacity: number;
  angle: number;
}

function ParticleCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: -999, y: -999 });
  const rafRef = useRef<number>(0);

  const init = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let W = canvas.offsetWidth;
    let H = canvas.offsetHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.scale(dpr, dpr);

    const particleCount = Math.min(Math.floor((W * H) / 9000), 140);
    const particles: Particle[] = [];
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * W,
        y: Math.random() * H,
        vx: (Math.random() - 0.5) * 0.6,
        vy: (Math.random() - 0.5) * 0.6,
        radius: Math.random() * 1.5 + 0.5,
        opacity: Math.random() * 0.5 + 0.3,
      });
    }

    const streamCount = Math.floor(W / 250);
    const streams: LightStream[] = [];
    for (let i = 0; i < streamCount; i++) {
      streams.push({
        x: Math.random() * W,
        y: Math.random() * H,
        speed: Math.random() * 1.2 + 0.4,
        length: Math.random() * 200 + 80,
        width: Math.random() * 1.5 + 0.5,
        opacity: Math.random() * 0.15 + 0.05,
        angle: (Math.random() - 0.5) * 0.3,
      });
    }

    const handleMouse = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    const handleLeave = () => {
      mouseRef.current = { x: -999, y: -999 };
    };

    canvas.addEventListener("mousemove", handleMouse);
    canvas.addEventListener("mouseleave", handleLeave);

    const connectDistance = 140;
    const mouseRepelRadius = 160;

    const animate = () => {
      ctx.clearRect(0, 0, W, H);
      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;

      // Draw light streams first (behind particles)
      streams.forEach((s) => {
        ctx.save();
        ctx.globalAlpha = s.opacity;
        ctx.strokeStyle = "#e8e8e8";
        ctx.lineWidth = s.width;
        ctx.lineCap = "round";
        ctx.shadowBlur = 12;
        ctx.shadowColor = "rgba(255,255,255,0.3)";

        const endX = s.x + Math.cos(s.angle) * s.length;
        const endY = s.y + Math.sin(s.angle) * s.length;
        const grad = ctx.createLinearGradient(s.x, s.y, endX, endY);
        grad.addColorStop(0, "rgba(255,255,255,0)");
        grad.addColorStop(0.3, "rgba(255,255,255,0.6)");
        grad.addColorStop(0.7, "rgba(255,255,255,0.6)");
        grad.addColorStop(1, "rgba(255,255,255,0)");
        ctx.strokeStyle = grad;

        ctx.beginPath();
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(endX, endY);
        ctx.stroke();
        ctx.restore();

        s.x += Math.cos(s.angle) * s.speed;
        s.y += Math.sin(s.angle) * s.speed;
        if (s.x > W + s.length) { s.x = -s.length; s.y = Math.random() * H; }
        if (s.x < -s.length) { s.x = W + s.length; s.y = Math.random() * H; }
        if (s.y > H + s.length) { s.y = -s.length; s.x = Math.random() * W; }
        if (s.y < -s.length) { s.y = H + s.length; s.x = Math.random() * W; }
      });

      // Update and draw particles
      particles.forEach((p) => {
        // Mouse repel
        const dx = p.x - mx;
        const dy = p.y - my;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < mouseRepelRadius && dist > 0) {
          const force = (mouseRepelRadius - dist) / mouseRepelRadius;
          p.vx += (dx / dist) * force * 0.15;
          p.vy += (dy / dist) * force * 0.15;
        }

        p.x += p.vx;
        p.y += p.vy;

        // Damping
        p.vx *= 0.99;
        p.vy *= 0.99;

        // Bounce
        if (p.x < 0 || p.x > W) p.vx *= -1;
        if (p.y < 0 || p.y > H) p.vy *= -1;
        p.x = Math.max(0, Math.min(W, p.x));
        p.y = Math.max(0, Math.min(H, p.y));

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${p.opacity})`;
        ctx.fill();
      });

      // Draw connections
      ctx.lineWidth = 0.6;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < connectDistance) {
            const alpha = (1 - dist / connectDistance) * 0.25;
            ctx.strokeStyle = `rgba(255,255,255,${alpha})`;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    const handleResize = () => {
      W = canvas.offsetWidth;
      H = canvas.offsetHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(rafRef.current);
      canvas.removeEventListener("mousemove", handleMouse);
      canvas.removeEventListener("mouseleave", handleLeave);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const cleanup = init();
    return cleanup;
  }, [init]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-auto"
      style={{ opacity: 0.9 }}
    />
  );
}

export default function HeroSection() {
  const [loaded, setLoaded] = useState(false);
  const { settings } = useSiteSettings();
  // 邀請制（預設）時不再以「加入我們」招募，改為引導至會員專區
  const applicationsOpen = settings.membership.applications_open;
  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col bg-navy-950 overflow-hidden">
      {/* Canvas Particle Network */}
      <ParticleCanvas />

      {/* Background Image - overlay on canvas */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 lg:left-[45%]">
          <SmartImage
            src="https://readdy.ai/api/search-image?query=Abstract%20futuristic%20AI%20network%20visualization%20with%20interconnected%20nodes%20and%20flowing%20data%20streams%2C%20geometric%20patterns%2C%20glowing%20connections%20in%20dark%20navy%20and%20silver%20white%20tones%2C%20clean%20minimalist%20style%2C%20technology%20concept%20art%2C%20professional%20corporate%20aesthetic%2C%20Taiwan%20island%20silhouette%20subtly%20integrated%2C%20high%20contrast%20elegant%20composition&width=800&height=800&seq=taiaa-hero-main&orientation=squarish"
            alt="AI Network"
            className="w-full h-full object-cover opacity-20 lg:opacity-30"
            loading="eager"
            hideOnError
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-950 via-navy-950/90 to-transparent lg:via-navy-950/80" />
        </div>
      </div>

      {/* Subtle mesh overlay */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]">
        <div
          className="w-full h-full animate-grid-move"
          style={{
            backgroundImage:
              "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 flex items-center px-6 md:px-10 lg:px-16 pt-20 md:pt-24">
        <div className="max-w-3xl">
          <div
            className={`mb-4 transition-all duration-700 ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-semibold text-white leading-[1.1] tracking-tight">
              TAIAA 台灣 AI 應用推廣協會
            </h1>
          </div>

          <p
            className={`text-xl sm:text-2xl md:text-3xl font-semibold text-white leading-snug transition-all duration-700 delay-150 ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            推動 AI 應用落地・<span className="text-silver-300">連結台灣產業未來</span>
          </p>

          <p
            className={`mt-6 md:mt-8 text-base md:text-lg lg:text-xl text-silver-400 leading-relaxed max-w-xl transition-all duration-700 delay-300 ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            TAIAA 致力於促進 AI 應用發展、產業交流、人才培育與跨界合作，協助企業、專業人士與教育單位理解 AI、導入 AI、善用 AI。
          </p>

          <div
            className={`mt-10 md:mt-12 flex flex-wrap gap-4 transition-all duration-700 delay-[450ms] ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <Link
              to="/membership"
              className="inline-flex items-center gap-2 bg-white text-navy-950 px-8 py-4 rounded-full text-sm font-semibold hover:bg-silver-100 transition-colors whitespace-nowrap tracking-wide"
            >
              {applicationsOpen ? "加入我們" : "會員專區"}
              <Icon className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 border border-white/30 text-white px-8 py-4 rounded-full text-sm font-medium hover:bg-white/10 transition-colors whitespace-nowrap tracking-wide"
            >
              了解更多
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom bar: 成果數據整理中（原四項統計數字待協會核實後再恢復） */}
      <div className="relative z-10 border-t border-white/10">
        <div className="px-6 md:px-10 lg:px-16 py-6 md:py-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="flex items-center gap-2 text-white text-sm md:text-base font-medium tracking-wide">
              <Icon className="ri-time-line text-silver-400" />
              會員單位、年度活動、合作夥伴與培育人才等成果數據整理中
            </p>
            <p className="text-sm text-silver-500">彙整完成後將於本站公布</p>
          </div>
        </div>
      </div>
    </section>
  );
}